import template from './ps-field-selector.html';
import './ps-field-selector.css';

/**
 * ViewModel for the PS Field Selector component.
 *
 * PSFieldSelectorViewModel manages a dropdown interface for selecting and reordering
 * table fields. It provides functionality to toggle field visibility, reorder fields,
 * and handles fields that are locked from being hidden or moved.
 *
 * @class PSFieldSelectorViewModel
 * @example
 * // Basic usage
 * const viewModel = new PSFieldSelectorViewModel({
 *   fields: [
 *     { field: 'id', name: 'ID', visible: true, locked: false },
 *     { field: 'name', name: 'Name', visible: true, locked: false }
 *   ],
 *   visible_fields: ko.observableArray(['id', 'name']),
 *   resetFields: () => console.log('Reset fields')
 * }, document.getElementById('field-selector'));
 *
 * // Advanced usage with custom field configuration
 * const viewModel = new PSFieldSelectorViewModel({
 *   fields: tableFields,
 *   visible_fields: (visibleFields) => updateTableColumns(visibleFields),
 *   resetFields: () => resetTableFields()
 * }, rootElement);
 */
class PSFieldSelectorViewModel
{
	/**
	 * Creates a new PSFieldSelectorViewModel instance.
	 *
	 * @param {Object} params - Field selector configuration parameters
	 * @param {Array<Object>} params.fields - Array of field objects with name, label, visible, and locked properties
	 * @param {ko.ObservableArray|Function} [params.visible_fields] - ObservableArray to receive visible field names
	 * @param {Function} [params.resetFields] - Optional callback function to reset field settings
	 * @param {HTMLElement} rootElement - The root DOM element for the component
	 */
	constructor(params, rootElement)
	{
		this.params = params;
		this.rootElement = rootElement;

		// Input params
		this.fields = ko_helper.safe_observableArray(params.fields, []);
		this.visible_field_names = ko_helper.safe_observable(params.visible_fields, []);
		this.resetFields = params.resetFields ?? null;

		// Internal propeties
		this.internal_fields = ko.observableArray();
		this.isOpen = ko.observable(false);

		// Event handlers.
		// Listen to document click events to make sure that the dropdown closes when clicking outside of it.
		// TODO: Check why disposal logic doesn't trigger when the component is removed.
		this.clickHandler = (e) =>
		{
			if (this.isOpen() && !this.rootElement.contains(e.target))
			{
				this.isOpen(false);
			}
		};
		document.addEventListener('click', this.clickHandler.bind(this));

		// Update the internal fields when the fields observable changes.
		this.fields.subscribe(() =>
		{
			this.update_internal_fields();
		});
		this.update_internal_fields();
	}

	/*
	 * Update the internal fields when the fields observable changes. This function will convert the fields value
	 * to a list of observable objects that can be used to track the visibility of the fields in the format that the component
	 * expects. This function should not affect the original fields values in any way.
	 */
	update_internal_fields()
	{
		let fields = this.fields();
		let visible_fields = this.visible_field_names() ?? [];

		let internal = fields.map(field =>
		{
			return {
				name: ko.observable(ko.unwrap(field.name) ?? ''),
				visible: ko.observable(ko.unwrap(field.visible) ?? true),
				locked: ko.observable(ko.unwrap(field.locked) ?? false)
			};
		});

		// Apply existing visible fields filter
		let internal_sorted = [];
		if (visible_fields.length > 0)
		{
			// TODO: Handle ordering of fields that do not start off as locked, or hidden.
			visible_fields.forEach(field_name =>
			{
				let field = internal.find(field => field.name() === field_name);
				if (field)
				{
					field.visible(true);
					internal_sorted.push(field);
				}
			});

			// Add any fields that are not in the visible fields list to the end of the list.
			let unassigned = fields.filter(field => visible_fields.indexOf(field.name()) === -1);
			unassigned.forEach(field => { field.visible(false); });
			internal_sorted.push(...unassigned);
		}
		else
		{
			internal_sorted = internal;
		}

		this.internal_fields(internal_sorted);

		if (this.internal_fields.length > 0)
			this.update_visible_field_names();
	}

	/*
	 * Update the visible field names whenever the internal fields change. This function will be the primary way to
	 * update values in the parent component or page.
	 */
	update_visible_field_names()
	{
		let fields = this.internal_fields();
		let visible_fields = fields.filter(field => field.visible()).map(field => field.name());
		this.visible_field_names(visible_fields);
	}

	/*
	 * Disposal logic. This should run to release the click handler from the document node.
	 * TODO: Check why disposal logic doesn't trigger when the component is removed from the DOM
	 */
	dispose()
	{
		document.removeEventListener('click', this.clickHandler);
	}

	/*
	 * Toggle the visibility of a field and update all of the relevant observables.
	 */
	toggle_visible(field)
	{
		if (field.locked())
			return;

		field.visible(!field.visible());
		this.update_visible_field_names();
	}

	/*
	 * Move a field up in the list and update all of the relevant observables.
	 */
	moveUp(field)
	{
		if (field.locked())
			return;

		let fields = this.internal_fields();
		const index = fields.indexOf(field);
		if (index > 0)
		{
			fields.splice(index - 1, 2, field, fields[index - 1]);
		}

		this.internal_fields(fields);
		this.update_visible_field_names();
	}

	/*
	 * Move a field down in the list and update all of the relevant observables.
	 */
	moveDown(field)
	{
		if (field.locked())
			return;

		let fields = this.internal_fields();
		const index = fields.indexOf(field);
		if (index < fields.length - 1)
		{
			fields.splice(index, 2, fields[index + 1], field);
		}

		this.internal_fields(fields);
		this.update_visible_field_names();
	}

	/*
	 * Reset the field settings to the default values. This will reset the visible fields to all of the original fields, or
	 * will call the resetFields function if it is provided to do the reset.
	 */
	resetFieldSettings()
	{
		if (typeof this.resetFields === 'function')
			this.resetFields();
		else
		{
			this.internal_fields().forEach(field =>
			{
				field.visible(true);
			});

			this.update_visible_field_names();
		}
	}
}

export default {
	name: 'ps-field-selector',
	viewModel: PSFieldSelectorViewModel,
	module_type: 'ko',
	template: template
};
